import React from 'react';
import { motion } from 'framer-motion';
import { Layers, ArrowLeftRight, Zap } from 'lucide-react';
import { AnimatedGear, AnimatedCode, AnimatedLightbulb } from '@/components/AnimatedGraphics';

const advantages = [
  {
    title: 'The 80/20 AI Protocol',
    description:
      'Traditional agencies waste weeks on repetitive code. Our AI handles 80% of the foundation instantly, so our humans can perfect the 20% that makes your app unique.',
    icon: Layers,
    metric: '80%',
    metricLabel: 'AI-accelerated',
    accent: 'blue',
    AnimatedIcon: AnimatedGear,
  },
  {
    title: 'Direct Vibe-to-Code',
    description:
      'We eliminated the UX-to-Dev handoff bottleneck. Once you approve a design, our proprietary workflow translates it directly into production-ready front-end code.',
    icon: ArrowLeftRight,
    metric: '0',
    metricLabel: 'Handoff delays',
    accent: 'orange',
    AnimatedIcon: AnimatedCode,
  },
  {
    title: 'The Version 1 Philosophy',
    description:
      'We refuse to build bloated, 6-month monoliths. We launch your core product fast so you can test, validate with real users, and generate revenue immediately.',
    icon: Zap,
    metric: 'V1',
    metricLabel: 'Ship fast, iterate',
    accent: 'blue',
    AnimatedIcon: AnimatedLightbulb,
  },
];

const accentStyles = {
  blue: {
    iconBg: 'bg-brand-blue/8',
    iconColor: 'text-brand-blue',
    metricColor: 'text-brand-blue',
    borderHover: 'hover:border-brand-blue/30',
  },
  orange: {
    iconBg: 'bg-brand-orange/8',
    iconColor: 'text-brand-orange',
    metricColor: 'text-brand-orange',
    borderHover: 'hover:border-brand-orange/30',
  },
};

export const SpeedAdvantage = () => {
  return (
    <section
      id="speed-advantage"
      className="relative py-16 sm:py-20 lg:py-24 bg-secondary/30 border-t border-[color:var(--hairline)] overflow-hidden"
      data-testid="speed-advantage-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-xs font-semibold text-brand-orange uppercase tracking-wider">
              Why We're Different
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              The Speed Advantage
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Three proprietary principles that let us deliver in days what traditional agencies take months to build.
            </p>
          </motion.div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {advantages.map((advantage, index) => {
            const Icon = advantage.icon;
            const style = accentStyles[advantage.accent];
            const AnimIcon = advantage.AnimatedIcon;
            const testIdMap = {
              'The 80/20 AI Protocol': 'speed-advantage-card-ai',
              'Direct Vibe-to-Code': 'speed-advantage-card-vibe',
              'The Version 1 Philosophy': 'speed-advantage-card-v1',
            };

            return (
              <motion.div
                key={advantage.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1],
                  delay: index * 0.1,
                }}
                whileHover={{ y: -3 }}
                className={`group relative bg-white rounded-2xl border border-[color:var(--hairline)] ${style.borderHover} p-5 sm:p-6 transition-shadow transition-colors duration-200 hover:shadow-[var(--shadow-lift)] cursor-default overflow-hidden`}
                data-testid={testIdMap[advantage.title]}
              >
                {/* Subtle animated icon in background */}
                <div className="absolute -top-2 -right-2 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-300">
                  <AnimIcon size={80} />
                </div>

                {/* Icon */}
                <div className={`relative z-10 w-11 h-11 rounded-xl ${style.iconBg} flex items-center justify-center mb-5`}>
                  <Icon className={`w-5 h-5 ${style.iconColor}`} strokeWidth={2} />
                </div>

                {/* Metric */}
                <div className="relative z-10 mb-4">
                  <span className={`text-3xl font-extrabold ${style.metricColor} tracking-tight`}>
                    {advantage.metric}
                  </span>
                  <span className="ml-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {advantage.metricLabel}
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative z-10 text-base font-semibold text-foreground mb-2">
                  {advantage.title}
                </h3>

                {/* Description */}
                <p className="relative z-10 text-sm leading-relaxed text-muted-foreground">
                  {advantage.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
